import { useState } from "react";
import { BookOpen, Clock, Zap, Users, Brain, Target, TrendingUp, ArrowLeft, Repeat, BarChart3, Shield, Lightbulb, Layout, Laptop, Globe, Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const ARTICLES = [
  {
    id: "kanban-method",
    title: "The Kanban Method: A Complete Guide for Teams",
    excerpt: "Learn how to implement Kanban boards to visualize work, limit work in progress, and maximize team efficiency.",
    category: "Methodology",
    readTime: "5 min read",
    icon: Target,
    color: "text-blue-500",
    bg: "bg-blue-500/10",
    content: [
      "Kanban is a visual workflow management method that helps teams see their work, limit work-in-progress (WIP), and continuously improve their process.",
      "The core principles are simple: visualize your work on a board with columns representing stages (To Do, In Progress, Done), limit how many items are in each stage, and focus on flow rather than individual utilization.",
      "With Opero Smart's Kanban boards, you can create custom columns that match your team's workflow, drag tasks between stages, and see at a glance where bottlenecks are forming.",
      "Start with three columns and expand as needed. The key is to keep it simple and iterate. Most teams find that 4-6 columns cover their entire workflow.",
      "Pro tip: Use WIP limits to prevent overload. If your 'In Progress' column constantly has more than 5 items, that's a signal to finish existing work before starting new tasks."
    ]
  },
  {
    id: "ai-productivity",
    title: "How AI is Transforming Task Management in 2026",
    excerpt: "Discover how AI assistants can parse natural language into structured tasks, suggest priorities, and generate subtasks automatically.",
    category: "AI & Automation",
    readTime: "4 min read",
    icon: Brain,
    color: "text-purple-500",
    bg: "bg-purple-500/10",
    content: [
      "AI is no longer just a buzzword in project management — it's a practical tool that saves hours of manual work every week.",
      "Natural language task parsing means you can type 'Call John tomorrow at 3pm about the Q2 budget review' and get a structured task with a title, due date, and description automatically.",
      "AI-generated subtasks break complex tasks into actionable steps. Instead of staring at 'Launch marketing campaign', the AI suggests specific steps like 'Draft email copy', 'Design landing page', and 'Set up tracking pixels'.",
      "Priority suggestion uses context about your existing workload, deadlines, and dependencies to recommend whether a new task should be high, medium, or low priority.",
      "Opero Smart's AI assistant is powered by GPT-5.2 and is available to all Pro users. Try it with the AI Quick Add bar at the top of your board."
    ]
  },
  {
    id: "remote-team-tips",
    title: "5 Strategies for Managing Remote Teams Effectively",
    excerpt: "Best practices for keeping distributed teams aligned, productive, and connected using modern project management tools.",
    category: "Team Management",
    readTime: "6 min read",
    icon: Users,
    color: "text-emerald-500",
    bg: "bg-emerald-500/10",
    content: [
      "Remote work is here to stay, and the teams that thrive are the ones with the right systems in place.",
      "Strategy 1: Daily standups via your board. Instead of long meetings, have each team member update their task statuses on the Kanban board. Everyone sees progress without scheduling a call.",
      "Strategy 2: Use @mentions for targeted communication. Rather than sending messages in a general chat, mention specific people on task comments so context stays attached to the work.",
      "Strategy 3: Set clear due dates and priorities. Remote teams need explicit expectations since there's no office walkby to check on progress.",
      "Strategy 4: Use shared projects for cross-team visibility. When marketing and engineering can see each other's boards, alignment happens naturally.",
      "Strategy 5: Review analytics weekly. Use dashboard metrics like completion rate, overdue tasks, and time-per-task to identify blockers before they become problems."
    ]
  },
  {
    id: "time-management",
    title: "Time Tracking: Why Every Freelancer Needs It",
    excerpt: "How built-in time tracking helps freelancers bill accurately, identify time sinks, and improve project estimates.",
    category: "Productivity",
    readTime: "4 min read",
    icon: Clock,
    color: "text-teal-500",
    bg: "bg-teal-500/10",
    content: [
      "If you're a freelancer and you're not tracking time, you're almost certainly undercharging.",
      "Built-in time tracking eliminates the friction of switching to a separate tool. Start the timer when you begin work, stop it when you're done. Your hours are automatically logged against the task.",
      "Over time, you'll build a data-driven picture of how long tasks actually take versus your estimates. Most freelancers find they underestimate by 30-50%.",
      "Time reports help you create accurate proposals for future projects. Instead of guessing, you can say 'Based on past projects, this will take approximately 40 hours.'",
      "Opero Smart's time tracking is available on every task for Pro users. Start tracking today and see where your hours really go."
    ]
  },
  {
    id: "gantt-charts",
    title: "Gantt Charts vs Kanban: When to Use Each",
    excerpt: "Understanding when to use timeline views versus board views for maximum project visibility and planning accuracy.",
    category: "Planning",
    readTime: "5 min read",
    icon: TrendingUp,
    color: "text-amber-500",
    bg: "bg-amber-500/10",
    content: [
      "Kanban and Gantt charts aren't competitors — they're complementary views of the same work.",
      "Use Kanban boards for day-to-day execution. They're perfect for seeing what's in progress right now, what's blocked, and what's coming up next. The visual simplicity keeps teams focused.",
      "Use Gantt charts for planning and stakeholder communication. When you need to show how tasks relate to each other over time, dependencies between items, and the critical path to a deadline, Gantt is unbeatable.",
      "The best teams switch between both views depending on the context. Sprint planning? Kanban. Quarterly roadmap? Gantt. Client update? Gantt. Daily standup? Kanban.",
      "Opero Smart includes both views, and they stay in sync. Move a task on the Kanban board and its Gantt chart position updates automatically."
    ]
  },
  {
    id: "getting-started",
    title: "Getting Started with Opero Smart: A 5-Minute Setup Guide",
    excerpt: "Everything you need to know to set up your first project, invite your team, and start managing tasks like a pro.",
    category: "Getting Started",
    readTime: "3 min read",
    icon: Zap,
    color: "text-orange-500",
    bg: "bg-orange-500/10",
    content: [
      "Welcome to Opero Smart! Here's how to go from zero to productive in under 5 minutes.",
      "Step 1: Sign in with Google. Your account is created instantly with 5 default columns: Backlog, To Do, In Progress, Review, and Done. You can customize these anytime.",
      "Step 2: Create your first project. Go to Projects in the sidebar and click '+ New Project'. Give it a name, pick a color, and optionally assign it to a team.",
      "Step 3: Add tasks. Use the '+ Task' button or try the AI Quick Add bar — type a natural language description and let AI parse it into a structured task with priorities and due dates.",
      "Step 4: Invite your team. Create a Team, add members by email, and they'll be able to collaborate on shared projects in real-time. You'll see their cursors on the board!",
      "Step 5: Explore features. Check out the Dashboard for analytics, Calendar for deadline overview, and Timeline for Gantt chart planning. Upgrade to Pro when you're ready for unlimited everything."
    ]
  },
  {
    id: "notion-alternatives",
    title: "7 Best Notion Alternatives for Project Management in 2026",
    excerpt: "Looking beyond Notion? Compare the top project management tools including Opero Smart, Asana, Trello, and more.",
    category: "Comparisons",
    readTime: "7 min read",
    icon: Layout,
    color: "text-rose-500",
    bg: "bg-rose-500/10",
    content: [
      "Notion is a fantastic all-in-one workspace, but it's not always the best fit for dedicated project management. If you need focused task tracking with features like Kanban boards, Gantt charts, and real-time collaboration, here are the top alternatives.",
      "1. Opero Smart ($7.99/mo) — Built specifically for project management with AI task parsing, real-time collaboration, Gantt charts, and time tracking. 20% cheaper than competitors with a generous free plan.",
      "2. Asana ($10.99/mo) — A well-established tool with great workflow automation. However, it lacks built-in AI assistance and real-time presence indicators.",
      "3. Trello ($10/mo) — Simple and intuitive Kanban boards. Great for small teams but limited analytics and no Gantt chart support without third-party add-ons.",
      "4. Monday.com ($9/mo) — Visual and customizable, but can feel overwhelming for smaller teams. Pricing scales quickly as you add users.",
      "5. ClickUp ($7/mo) — Feature-rich but complex. The learning curve is steep, and the interface can feel cluttered compared to more focused tools.",
      "6. Linear ($8/mo) — Excellent for software development teams with GitHub integration. Less suitable for non-technical teams.",
      "7. Basecamp ($15/mo flat) — Simple project management with a unique flat-pricing model. Lacks advanced features like Gantt charts and AI.",
      "For teams that want the best balance of features, simplicity, and price, Opero Smart stands out with its AI assistant, real-time collaboration, and competitive pricing."
    ]
  },
  {
    id: "ai-project-management",
    title: "How AI is Revolutionizing Project Management in 2026",
    excerpt: "From natural language task creation to predictive analytics, discover how AI tools like Opero Smart are transforming how teams work.",
    category: "AI & Trends",
    readTime: "6 min read",
    icon: Brain,
    color: "text-violet-500",
    bg: "bg-violet-500/10",
    content: [
      "Artificial intelligence is no longer a futuristic concept in project management — it's a practical tool that's saving teams hours every week in 2026.",
      "Natural Language Processing (NLP) allows you to type 'Schedule team meeting next Friday to discuss Q3 roadmap' and get a fully structured task with title, due date, description, and suggested priority. Opero Smart's GPT-5.2 powered assistant does exactly this.",
      "AI-generated subtasks break complex projects into manageable pieces. Instead of manually thinking through every step, the AI analyzes your task and suggests a complete breakdown — saving 15-30 minutes per complex task.",
      "Smart priority suggestions analyze your workload, deadlines, and team capacity to recommend whether a new task should be high, medium, or low priority. This reduces decision fatigue and keeps teams focused on what matters.",
      "Predictive analytics can identify potential bottlenecks before they happen. By analyzing historical completion rates and current workloads, AI can flag when a deadline is at risk.",
      "The key to adopting AI in project management is starting simple. Use AI for task creation first, then expand to subtask generation and priority suggestions. Tools like Opero Smart make this transition seamless with built-in AI that works right from the task creation bar."
    ]
  },
  {
    id: "remote-productivity",
    title: "The Ultimate Guide to Remote Team Productivity in 2026",
    excerpt: "Proven strategies, tools, and workflows for keeping distributed teams aligned and shipping consistently.",
    category: "Productivity",
    readTime: "8 min read",
    icon: Globe,
    color: "text-cyan-500",
    bg: "bg-cyan-500/10",
    content: [
      "Remote work has become the default for millions of teams worldwide. But productivity in distributed teams requires intentional systems — not just video calls and chat messages.",
      "The Async-First Approach: The most productive remote teams minimize real-time meetings. Instead, they use tools like Opero Smart to communicate through task comments, @mentions, and status updates. This lets people work in their own time zones without blocking each other.",
      "Visibility Without Surveillance: Remote managers need to see progress without micromanaging. A well-maintained Kanban board shows exactly what's happening across the team — who's working on what, what's blocked, and what's done. Opero Smart's live presence indicators show who's online without being intrusive.",
      "Structured Daily Standups: Replace 30-minute video standups with async updates. Each team member moves their tasks on the board and adds a brief comment. Takes 2 minutes instead of 30.",
      "Weekly Reviews with Data: Use dashboard analytics to review completion rates, time tracking data, and bottleneck patterns. Data-driven retrospectives are more productive than subjective discussions.",
      "The Right Tool Stack: At minimum, remote teams need: a project management tool (Opero Smart), a communication tool (Slack/Teams), a documentation tool (Notion/Google Docs), and a video call tool (Zoom/Google Meet). Keep the stack small — too many tools create more problems than they solve.",
      "Timezone-Friendly Workflows: Set up your Kanban board with columns that represent handoff points between timezone groups. Tasks flow from the first timezone to the next naturally, creating a 24-hour productivity cycle.",
      "The result? Teams that implement these practices consistently report 20-30% higher output while working fewer hours than their office-bound counterparts."
    ]
  },
  {
    id: "task-management-mistakes",
    title: "10 Task Management Mistakes That Kill Team Productivity",
    excerpt: "Common pitfalls that slow teams down and how to fix them with better workflows and tools.",
    category: "Best Practices",
    readTime: "5 min read",
    icon: Settings,
    color: "text-red-500",
    bg: "bg-red-500/10",
    content: [
      "Even experienced teams fall into task management traps that silently drain productivity. Here are the top 10 mistakes and how to fix them.",
      "1. No Single Source of Truth — Tasks scattered across emails, chat, and spreadsheets. Fix: Use one tool (like Opero Smart) as the central hub for all tasks.",
      "2. Too Many Columns — Having 12 status columns creates confusion. Fix: Start with 4-5 columns (Backlog, To Do, In Progress, Review, Done) and only add more if genuinely needed.",
      "3. No Due Dates — Tasks without deadlines never get done. Fix: Set realistic due dates on every task. Use calendar views to spot conflicts.",
      "4. Giant Tasks — 'Build the new website' isn't a task, it's a project. Fix: Break everything into tasks that take 1-4 hours. Use AI subtask generation to help.",
      "5. No Priorities — When everything is high priority, nothing is. Fix: Use a simple high/medium/low system and limit high-priority items to 3-5 at a time.",
      "6. Ignoring WIP Limits — Having 20 tasks 'In Progress' means nothing is actually progressing. Fix: Limit each person to 2-3 active tasks maximum.",
      "7. No Regular Reviews — Boards that aren't maintained become graveyards. Fix: Weekly 15-minute reviews to archive stale tasks and update priorities.",
      "8. Missing Context — Tasks that say 'Fix the thing' help nobody. Fix: Include descriptions, subtasks, and links. Attach files and screenshots directly to tasks.",
      "9. Not Using Templates — Recreating recurring task structures wastes time. Fix: Save common task patterns as templates (available in Opero Smart Pro).",
      "10. No Automation — Manual status updates and notifications are error-prone. Fix: Use tools with automatic notifications, recurring tasks, and AI assistance."
    ]
  },
  {
    id: "kanban-vs-scrum",
    title: "Kanban vs Scrum: Which Methodology is Right for Your Team?",
    excerpt: "A practical comparison to help you choose the right agile framework for your project management needs.",
    category: "Methodology",
    readTime: "6 min read",
    icon: Repeat,
    color: "text-indigo-500",
    bg: "bg-indigo-500/10",
    content: [
      "Kanban and Scrum are both agile methodologies, but they solve different problems. Understanding the distinction helps you pick the right one for your team.",
      "Scrum is sprint-based: work is planned in 2-4 week cycles with fixed commitments. Teams commit to a set of tasks at the start and aim to complete them by the sprint end. It works best for teams that need predictable delivery timelines.",
      "Kanban is flow-based: work is continuous with no fixed cycles. New tasks are pulled from the backlog whenever capacity opens up. It works best for teams with unpredictable workloads — support teams, agencies, or teams handling mixed project/maintenance work.",
      "Key differences: Scrum has roles (Scrum Master, Product Owner), ceremonies (sprint planning, daily standups, retrospectives), and fixed sprint lengths. Kanban has no prescribed roles, fewer meetings, and focuses on WIP limits and flow metrics.",
      "Many teams use a hybrid approach called 'Scrumban' — they use Kanban boards with some Scrum ceremonies like weekly planning and retrospectives. Opero Smart supports this naturally with its flexible column system, due dates, and analytics.",
      "Start with Kanban if: you're new to agile, have variable workloads, or want minimal process overhead. Start with Scrum if: you need predictable delivery dates, have dedicated teams, and stakeholders want sprint-based progress reports.",
      "The beauty of using a tool like Opero Smart is that it supports both approaches without locking you in. Start with simple Kanban, and add sprint-like structures (using projects and due dates) as your team matures."
    ]
  },
  {
    id: "startup-project-management",
    title: "Project Management for Startups: A No-Nonsense Guide",
    excerpt: "How early-stage startups can stay organized without drowning in process. Practical tips from real founders.",
    category: "Startups",
    readTime: "5 min read",
    icon: Lightbulb,
    color: "text-yellow-500",
    bg: "bg-yellow-500/10",
    content: [
      "Startups don't need heavy project management processes. But they do need enough structure to avoid chaos. Here's how to find the right balance.",
      "Rule 1: One Board, One Source of Truth. Every task, bug, and idea goes on the board. If it's not on the board, it doesn't exist. This prevents the 'I thought you were doing that' conversations.",
      "Rule 2: Keep It Lean. Start with three columns: To Do, Doing, Done. Add more only when you feel pain. Most startups under 10 people never need more than 5 columns.",
      "Rule 3: Weekly Planning, Daily Glances. Spend 30 minutes every Monday planning the week's priorities. Then just glance at the board daily — no standup meetings needed until you're 5+ people.",
      "Rule 4: Use Free Tools Until They Hurt. Start with Opero Smart's free plan (50 tasks, Kanban board, calendar). It's more than enough for a founding team. Upgrade to Pro only when you need AI assistance, time tracking, or unlimited tasks.",
      "Rule 5: Automate Repetitive Work. Use task templates for recurring processes (deploy checklist, new customer onboarding, weekly reporting). This saves 30+ minutes per week.",
      "Rule 6: Don't Over-Categorize. You don't need 20 labels, 15 categories, and 8 priority levels. Start with high/medium/low priority and one or two tags. Add complexity only when simplicity fails.",
      "The best project management system for a startup is one that everyone actually uses. If it's too complex, people will revert to post-it notes and memory. Keep it simple, keep it visible, keep it updated."
    ]
  },
  {
    id: "data-driven-management",
    title: "Using Dashboard Analytics to Improve Team Performance",
    excerpt: "How to read project management metrics and use data to make better decisions about priorities and workload.",
    category: "Analytics",
    readTime: "5 min read",
    icon: BarChart3,
    color: "text-green-500",
    bg: "bg-green-500/10",
    content: [
      "Most project management tools show you data. The challenge is knowing what to do with it. Here's a practical guide to the metrics that actually matter.",
      "Completion Rate: This is your team's batting average — the percentage of tasks marked done vs total tasks. A healthy team maintains 70-85%. Below 60% means either tasks are too large, priorities aren't clear, or the team is overloaded.",
      "Cycle Time: How long tasks stay in 'In Progress'. If tasks linger for days, they're either too big (break them down) or blocked (identify and remove blockers). Opero Smart's analytics track this automatically.",
      "WIP Count: The number of tasks currently in progress. More isn't better — research shows that limiting WIP to 2-3 items per person significantly increases throughput and quality.",
      "Overdue Tasks: The number of tasks past their due date. A few is normal; more than 20% signals a planning problem. Either estimates are too optimistic or priorities keep shifting.",
      "Time Distribution: Where does your team's time actually go? Time tracking data reveals surprising patterns — most teams spend 30-40% of time on tasks they didn't plan for (interrupts, bugs, meetings).",
      "Daily Completions Trend: A flat or declining trend line means something is wrong. Look for patterns — do completions drop on certain days? After certain meetings? Use this to protect productive time.",
      "The goal isn't to maximize every metric — it's to use data to have better conversations. Instead of asking 'Why isn't this done?', you can ask 'I see our cycle time has doubled this week — what's blocking things?'"
    ]
  },
  {
    id: "security-privacy-pm",
    title: "Why Data Privacy Matters in Project Management Tools",
    excerpt: "How to evaluate security features when choosing a PM tool, and why per-user data isolation is critical.",
    category: "Security",
    readTime: "4 min read",
    icon: Shield,
    color: "text-slate-500",
    bg: "bg-slate-500/10",
    content: [
      "Your project management tool contains your entire business strategy — task descriptions, deadlines, team structures, client names, and strategic priorities. Choosing a tool with strong privacy isn't optional.",
      "Per-User Data Isolation: The most important security feature in a multi-tenant SaaS tool. Each user's data should be completely invisible to other users unless explicitly shared. Opero Smart implements this at the database level — every query is scoped by user ID.",
      "Authentication: Look for tools that use established auth providers (Google OAuth, SSO) rather than rolling their own password systems. Opero Smart uses Google OAuth, which means two-factor authentication is built in if users have it enabled on their Google account.",
      "Sharing Controls: When you do need to share, you should have granular control. Opero Smart offers password-protected share links with optional expiry dates — so you can share a project view with a client that automatically becomes inaccessible after 30 days.",
      "Data Portability: Can you export all your data? A good PM tool should offer CSV and JSON export so you're never locked in. Opero Smart includes both in the Pro plan.",
      "Encryption: Data should be encrypted both in transit (HTTPS) and at rest. Check that your PM tool's infrastructure provider meets standard security certifications.",
      "The bottom line: free tools often monetize through data. Paid tools with transparent pricing (like Opero Smart's $7.99/month Pro plan) align their business model with protecting your data, not exploiting it."
    ]
  }
];

export const BlogView = ({ linkMode = false, articleId = null }) => {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const activeId = linkMode ? articleId : selectedArticle;

  if (activeId) {
    const article = ARTICLES.find(a => a.id === activeId);
    if (!article) {
      return (
        <div className="p-10 text-center text-muted-foreground" data-testid="blog-article-not-found">
          Article not found. <a href="/blog" className="text-primary underline">Back to the blog</a>
        </div>
      );
    }
    const Icon = article.icon;
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        {linkMode ? (
          <Button asChild variant="ghost" size="sm" className="mb-6" data-testid="blog-back">
            <a href="/blog"><ArrowLeft className="h-4 w-4 mr-2" />Back to Articles</a>
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => setSelectedArticle(null)} className="mb-6" data-testid="blog-back">
            <ArrowLeft className="h-4 w-4 mr-2" />Back to Articles
          </Button>
        )}
        <article>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl ${article.bg} flex items-center justify-center`}>
              <Icon className={`h-5 w-5 ${article.color}`} />
            </div>
            <div>
              <Badge variant="outline" className="text-xs">{article.category}</Badge>
              <span className="text-xs text-muted-foreground ml-2">{article.readTime}</span>
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-6" data-testid="blog-article-title">{article.title}</h1>
          <div className="space-y-4">
            {article.content.map((para, i) => (
              <p key={i} className="text-muted-foreground leading-relaxed">{para}</p>
            ))}
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="relative rounded-2xl overflow-hidden mb-8 h-40 sm:h-52" data-testid="blog-header-banner">
        <img src="https://static.prod-images.emergentagent.com/jobs/488d4f94-f06c-450b-9416-83b7a331180f/images/e5281a83d68945ce2f0fd960631c46fed830fb0a293888e48c4fbee620cc7c44.png" alt="Resources & Guides" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-900/50 to-transparent" />
        <div className="relative h-full flex flex-col justify-center px-6 sm:px-8">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-1 flex items-center gap-2" data-testid="blog-title">
            <BookOpen className="h-5 w-5 text-teal-300" />Resources & Guides
          </h2>
          <p className="text-sm text-white/80 max-w-md">Tips, strategies, and guides to get the most out of Opero Smart</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ARTICLES.map(article => {
          const Icon = article.icon;
          const cardClass = "bg-card border rounded-xl p-5 text-left hover:shadow-md hover:border-primary/30 transition-all group block";
          const inner = (
            <>
              <div className={`w-10 h-10 rounded-xl ${article.bg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                <Icon className={`h-5 w-5 ${article.color}`} />
              </div>
              <Badge variant="outline" className="text-xs mb-2">{article.category}</Badge>
              <h3 className="font-semibold text-sm mb-1.5 line-clamp-2">{article.title}</h3>
              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{article.excerpt}</p>
              <span className="text-xs text-muted-foreground">{article.readTime}</span>
            </>
          );
          return linkMode ? (
            <a key={article.id} href={`/blog/${article.id}`} className={cardClass} data-testid={`blog-card-${article.id}`}>{inner}</a>
          ) : (
            <button key={article.id} onClick={() => setSelectedArticle(article.id)} className={cardClass} data-testid={`blog-card-${article.id}`}>{inner}</button>
          );
        })}
      </div>
    </div>
  );
};
